import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, AlertTriangle, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { apiService } from '../services/api';
import ProcessingStatus, { dimClass } from './ProcessingStatus';
import { Skel, SkelRows } from './Skeleton';
import { useLatestRequest, isCanceled } from '../utils/latestRequest';

const zoneColor = (si) => {
  if (si == null) return { bg: 'bg-dark-700', text: 'text-dark-400', label: '-' };
  if (si >= 71) return { bg: 'bg-green-900/40', text: 'text-green-400', label: 'GREEN' };
  if (si >= 31) return { bg: 'bg-dark-600', text: 'text-dark-300', label: 'GREY' };
  return { bg: 'bg-red-900/40', text: 'text-red-400', label: 'RED' };
};

const formatTimeOnly = (isoStr) => {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric', minute: '2-digit',
    hour12: true,
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const DEFAULT_DATE_FROM = () => {
  const d = new Date();
  d.setDate(d.getDate() - 15);
  return d.toISOString().slice(0, 10);
};

const SignalHistoryPanel = () => {
  const [data, setData] = useState([]);
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [symbolGroups, setSymbolGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [flipDetail, setFlipDetail] = useState(null);

  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [dateFrom, setDateFrom] = useState(DEFAULT_DATE_FROM);
  const [dateTo, setDateTo] = useState('');
  const [flipsOnly, setFlipsOnly] = useState(false);
  const [anomaliesOnly, setAnomaliesOnly] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [silentLoading, setSilentLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    try { return JSON.parse(localStorage.getItem('signalHistory.autoRefresh')) ?? true; } catch { return true; }
  });

  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    localStorage.setItem('signalHistory.autoRefresh', JSON.stringify(autoRefresh));
  }, [autoRefresh]);

  const dataReq = useLatestRequest();
  const fetchData = useCallback(async (silent = false) => {
    const req = dataReq();          // a newer filter click (or refresh) cancels this one
    if (!silent) setLoading(true);
    if (silent) setSilentLoading(true);
    setError(null);
    try {
      const params = {};
      if (selectedSymbols.length > 0) params.symbol = selectedSymbols.join(',');
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (flipsOnly) params.flips_only = 'true';
      if (anomaliesOnly) params.anomalies_only = 'true';

      const response = await apiService.getSignalHistory(params, { signal: req.signal });
      if (!isMountedRef.current || !req.isCurrent()) return;

      const results = response.data.results || [];
      setData(results);
      if (response.data.available_symbols) {
        setAvailableSymbols(response.data.available_symbols);
      }
      if (response.data.symbol_groups) {
        setSymbolGroups(response.data.symbol_groups);
      }

    } catch (err) {
      if (isMountedRef.current && req.isCurrent() && !isCanceled(err)) setError(err.message || 'Failed to fetch signal history');
    } finally {
      if (isMountedRef.current && req.isCurrent()) {
        // the newest request clears BOTH flags — a background refresh may have
        // replaced a clicked load, which must not leave its strip on forever
        setLoading(false);
        setSilentLoading(false);
        setLastRefreshed(new Date());
      }
    }
  }, [dataReq, selectedSymbols, dateFrom, dateTo, flipsOnly, anomaliesOnly]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh polling — cleanup on unmount or when disabled
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchData(true), 60000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, fetchData]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const toggleSymbol = (sym) => {
    setSelectedSymbols(prev =>
      prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym]
    );
  };

  const handleFlipClick = async (row) => {
    if (expandedRow === row.id) {
      setExpandedRow(null);
      setFlipDetail(null);
      return;
    }
    setExpandedRow(row.id);
    try {
      const response = await apiService.getSignalHistory({
        symbol: row.symbol,
        date_from: row.price_date,
        date_to: row.price_date,
      });
      const rows = (response.data.results || []).sort((a, b) =>
        new Date(b.computed_at) - new Date(a.computed_at)
      );
      setFlipDetail(rows);
    } catch (err) {
      console.error('Error fetching flip detail:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="dashboard-card px-4 py-4 space-y-3">
        {/* Symbol chips grouped by region — 2 rows */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* the index chips come with the first answer: their places shimmer until then */}
          {symbolGroups.length === 0 && availableSymbols.length === 0 && loading && (
            <div className="flex items-center gap-1.5" data-testid="symbols-pending">
              {[0, 1, 2, 3, 4, 5, 6].map(i => <Skel key={i} className="h-[30px] w-[72px]" />)}
            </div>
          )}
          {(symbolGroups.length > 0 ? symbolGroups : availableSymbols.length > 0 ? [{ region: 'All', symbols: availableSymbols }] : []).map(group => (
            <div key={group.region} className="flex items-center gap-1.5">
              <span className="text-xs text-primary-400 font-bold uppercase tracking-wide shrink-0">{group.region === 'United States' ? 'US' : group.region === 'Asia Pacific' ? 'APAC' : group.region.toUpperCase()}</span>
              <div className="w-px h-5 bg-dark-600" />
              {group.symbols.map(sym => (
                <button
                  key={sym}
                  onClick={() => toggleSymbol(sym)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                    selectedSymbols.includes(sym)
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'bg-dark-700/80 text-dark-400 hover:text-white hover:bg-dark-600'
                  }`}
                >
                  {sym}
                </button>
              ))}
            </div>
          ))}
          {selectedSymbols.length > 0 && (
            <button
              onClick={() => setSelectedSymbols([])}
              className="px-2 py-1 text-[11px] text-dark-400 hover:text-white underline underline-offset-2"
            >
              clear
            </button>
          )}
        </div>

        <div className="border-t border-dark-700" />

        {/* Date, toggles, refresh */}
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-dark-800 border border-dark-600 text-white text-[11px] rounded-md px-2 py-1 focus:outline-none focus:border-primary-500 w-[120px]"
          />
          <span className="text-dark-600 text-[11px]">–</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-dark-800 border border-dark-600 text-white text-[11px] rounded-md px-2 py-1 focus:outline-none focus:border-primary-500 w-[120px]"
          />

          <div className="h-5 w-px bg-dark-700" />

          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <button
              role="switch"
              aria-checked={flipsOnly}
              onClick={() => setFlipsOnly(!flipsOnly)}
              className={`relative w-7 h-4 rounded-full transition-colors ${flipsOnly ? 'bg-blue-500' : 'bg-dark-600'}`}
            >
              <span className={`absolute top-[2px] h-3 w-3 rounded-full bg-white transition-transform ${flipsOnly ? 'left-[14px]' : 'left-[2px]'}`} />
            </button>
            <span className="text-[11px] text-dark-300">Flips</span>
          </label>

          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <button
              role="switch"
              aria-checked={anomaliesOnly}
              onClick={() => setAnomaliesOnly(!anomaliesOnly)}
              className={`relative w-7 h-4 rounded-full transition-colors ${anomaliesOnly ? 'bg-amber-500' : 'bg-dark-600'}`}
            >
              <span className={`absolute top-[2px] h-3 w-3 rounded-full bg-white transition-transform ${anomaliesOnly ? 'left-[14px]' : 'left-[2px]'}`} />
            </button>
            <span className="text-[11px] text-dark-300">Anomalies</span>
          </label>

          <div className="flex items-center gap-2 ml-auto">
            <label className={`flex items-center gap-1.5 cursor-pointer select-none px-2 py-1 rounded-md border transition-all ${autoRefresh ? 'bg-green-500/15 border-green-500/40' : 'bg-transparent border-transparent'}`} title="Auto-refresh every 60 seconds with browser notifications on new flips">
              <button
                role="switch"
                aria-checked={autoRefresh}
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`relative w-7 h-4 rounded-full transition-colors ${autoRefresh ? 'bg-green-500' : 'bg-dark-600'}`}
              >
                <span className={`absolute top-[2px] h-3 w-3 rounded-full bg-white transition-transform ${autoRefresh ? 'left-[14px]' : 'left-[2px]'}`} />
              </button>
              <span className={`text-[11px] font-medium ${autoRefresh ? 'text-green-400' : 'text-dark-500'}`}>Auto Refresh</span>
            </label>

            <button
              onClick={() => fetchData()}
              disabled={loading}
              className="flex items-center gap-1 px-2.5 py-1 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-[11px] font-semibold transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      <ProcessingStatus active={loading} title="Loading signal history" className="" />
      {/* Results count + last refreshed */}
      <div className="text-xs text-dark-500 flex items-center gap-3">
        {/* first load: no count until the answer is in — "0 rows" would read as nothing found */}
        {loading && data.length === 0
          ? <Skel className="h-3 w-24" testId="rows-pending" />
          : <span>{data.length} row{data.length !== 1 ? 's' : ''} returned</span>}
        {lastRefreshed && (
          <span className="flex items-center gap-1">
            <span className={silentLoading ? 'text-green-400' : ''}>
              {silentLoading ? 'Refreshing…' : `Updated ${lastRefreshed.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}`}
            </span>
          </span>
        )}
        {data.filter(r => r.is_flip).length > 0 && (
          <span className="text-blue-400 font-semibold">
            {data.filter(r => r.is_flip).length} flip{data.filter(r => r.is_flip).length !== 1 ? 's' : ''}
          </span>
        )}
        {data.filter(r => r.is_anomaly).length > 0 && (
          <span className="text-amber-400 font-semibold">
            {data.filter(r => r.is_anomaly).length} anomal{data.filter(r => r.is_anomaly).length !== 1 ? 'ies' : 'y'}
          </span>
        )}
      </div>

      {/* Table */}
      <div className={`dashboard-card overflow-x-auto ${dimClass(loading && data.length > 0)}`}>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b-2 border-dark-600 text-dark-400 uppercase tracking-wider">
              <th className="text-left py-2.5 px-3 font-semibold">Symbol</th>
              <th className="text-left py-2.5 px-3 font-semibold">Time</th>
              <th className="text-right py-2.5 px-3 font-semibold">Price</th>
              <th className="text-center py-2.5 px-3 font-semibold">SI</th>
              <th className="text-center py-2.5 px-3 font-semibold">Source</th>
              <th className="text-left py-2.5 px-3 font-semibold">Signal</th>
              <th className="text-center py-2.5 px-3 font-semibold">Short</th>
            </tr>
          </thead>
          <tbody>
            {loading && data.length === 0 && <SkelRows cols={7} />}
            {data.map((row, rowIdx) => {
              const zone = zoneColor(row.smart_index_st);
              const isExpanded = expandedRow === row.id;

              // Detect zone flip: compare with previous row for same symbol+date (data sorted desc)
              const prevRow = rowIdx < data.length - 1 ? data[rowIdx + 1] : null;
              let flipInfo = null;
              if (prevRow && prevRow.symbol === row.symbol && prevRow.price_date === row.price_date) {
                const prevZone = prevRow.smart_index_st >= 71 ? 'GREEN' : prevRow.smart_index_st <= 30 ? 'RED' : 'GREY';
                const currZone = row.smart_index_st >= 71 ? 'GREEN' : row.smart_index_st <= 30 ? 'RED' : 'GREY';
                if (prevZone === 'RED' && currZone === 'GREEN') {
                  flipInfo = { text: '↗ BULLISH', color: 'bg-green-600', fromPrice: prevRow.close, fromSI: prevRow.smart_index_st };
                } else if (prevZone === 'GREEN' && currZone === 'RED') {
                  flipInfo = { text: '↘ BEARISH', color: 'bg-red-600', fromPrice: prevRow.close, fromSI: prevRow.smart_index_st };
                }
              }

              // Date separator: show a header row when the date changes
              const nextRow = rowIdx > 0 ? data[rowIdx - 1] : null;
              const showDateHeader = rowIdx === 0 || (nextRow && nextRow.price_date !== row.price_date);

              // Compute date summary: only show when a flip happened on this day
              // Shows potential reversal levels from the opposite zone
              let dateSummary = null;
              if (showDateHeader) {
                const dateRows = data.filter(r => r.price_date === row.price_date && r.symbol === row.symbol && r.close != null);
                const _z = (si) => si >= 71 ? 'GREEN' : si <= 30 ? 'RED' : 'GREY';

                // Check if any flip happened on this day
                const dateRowsAsc = [...dateRows].reverse();
                let hasFlip = false;
                for (let i = 1; i < dateRowsAsc.length; i++) {
                  const pz = _z(dateRowsAsc[i - 1].smart_index_st);
                  const cz = _z(dateRowsAsc[i].smart_index_st);
                  if ((pz === 'RED' && cz === 'GREEN') || (pz === 'GREEN' && cz === 'RED')) { hasFlip = true; break; }
                }

                if (hasFlip) {
                  const bearishPrices = dateRows.filter(r => _z(r.smart_index_st) === 'RED').map(r => r.close);
                  const bearishHigh = bearishPrices.length ? Math.max(...bearishPrices) : null;
                  const bullishPrices = dateRows.filter(r => _z(r.smart_index_st) === 'GREEN').map(r => r.close);
                  const bullishLow = bullishPrices.length ? Math.min(...bullishPrices) : null;
                  dateSummary = { bearishHigh, bullishLow };
                }

                // Short summary for this day
                const allDateRows = data.filter(r => r.price_date === row.price_date && r.symbol === row.symbol);
                const allDateRowsAsc = [...allDateRows].reverse(); // oldest first
                const firstEntryRow = allDateRowsAsc.find(r => r.short_signal === 'ENTRY');
                const shortExitRow = allDateRowsAsc.find(r => r.short_signal === 'EXIT');
                if (firstEntryRow || shortExitRow) {
                  dateSummary = dateSummary || {};
                  // Check if short is still active — latest row should have ENTRY or ACTIVE
                  const latestRow = allDateRows[0]; // desc order, [0] is latest
                  const shortStillActive = latestRow && (latestRow.short_signal === 'ENTRY' || latestRow.short_signal === 'ACTIVE');

                  if (firstEntryRow && shortStillActive) {
                    const entryPrice = firstEntryRow.close;
                    dateSummary.shortEntry = entryPrice;
                    const entryIdx = allDateRowsAsc.indexOf(firstEntryRow);
                    const rowsSinceEntry = allDateRowsAsc.slice(entryIdx).filter(r => r.close != null);
                    if (rowsSinceEntry.length > 0) {
                      dateSummary.shortLatest = rowsSinceEntry[rowsSinceEntry.length - 1].close;
                      if (entryPrice > 0) {
                        dateSummary.shortPnl = ((entryPrice - dateSummary.shortLatest) / entryPrice * 100).toFixed(2);
                      }
                    }
                  } else if (firstEntryRow && !shortStillActive && !shortExitRow) {
                    // Short was entered but later revoked (no EXIT, just disappeared)
                    dateSummary.shortRevoked = firstEntryRow.close;
                  }
                  if (shortExitRow) dateSummary.shortExit = shortExitRow.close;
                }
              }

              return (
                <React.Fragment key={row.id}>
                  {showDateHeader && (
                    <tr className="border-t-2 border-dark-500/50">
                      <td colSpan={7} className="py-3 px-3 bg-dark-800">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-bold text-sm flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-primary-400" />
                            {formatDate(row.price_date)}
                          </span>
                          {dateSummary && (
                            <div className="flex items-center gap-3">
                              {dateSummary.shortEntry != null && (
                                <div className="flex items-center gap-3 px-3 py-1.5 rounded-lg text-[11px] bg-orange-900/30 border border-orange-700/30">
                                  <span className="text-orange-400 font-bold">▼ Short @ {dateSummary.shortEntry.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                  {dateSummary.shortLatest != null && dateSummary.shortPnl != null && (
                                    <span className={`font-bold tabular-nums ${parseFloat(dateSummary.shortPnl) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      Now {dateSummary.shortLatest.toLocaleString(undefined, { minimumFractionDigits: 2 })} ({parseFloat(dateSummary.shortPnl) > 0 ? '+' : ''}{dateSummary.shortPnl}%)
                                    </span>
                                  )}
                                </div>
                              )}
                              {dateSummary.shortRevoked != null && (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] bg-dark-600 border border-dark-500">
                                  <span className="text-dark-400 font-bold">▼ Short @ {dateSummary.shortRevoked.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                  <span className="text-amber-400 font-bold">Revoked</span>
                                </div>
                              )}
                              {dateSummary.shortExit != null && (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] bg-yellow-900/30 border border-yellow-700/30">
                                  <span className="text-yellow-400 font-bold">✓ Short Cover</span>
                                  <span className="text-white font-semibold tabular-nums">{dateSummary.shortExit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                              )}
                              {dateSummary.bearishHigh != null && (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] bg-red-900/30 border border-red-700/30">
                                  <span className="text-red-400 font-bold">Bearish Level</span>
                                  <span className="text-white font-semibold tabular-nums">{dateSummary.bearishHigh.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                              )}
                              {dateSummary.bullishLow != null && (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] bg-green-900/30 border border-green-700/30">
                                  <span className="text-green-400 font-bold">Bullish Level</span>
                                  <span className="text-white font-semibold tabular-nums">{dateSummary.bullishLow.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  <tr
                    className={`border-b border-dark-700/50 hover:bg-dark-700/30 transition-colors ${
                      flipInfo ? 'border-l-2 border-l-blue-400 bg-blue-900/10' :
                      row.is_anomaly ? 'border-l-2 border-l-amber-500 bg-amber-900/10' : ''
                    }`}
                  >
                    <td className="py-2 px-3">
                      <span className="px-2 py-0.5 rounded bg-dark-600 text-white font-medium">{row.symbol}</span>
                    </td>
                    <td className="py-2 px-3 text-dark-300 tabular-nums">{formatTimeOnly(row.computed_at)}</td>
                    <td className="py-2 px-3 text-right text-dark-200 tabular-nums">
                      {row.close != null ? row.close.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${zone.bg} ${zone.text}`}>
                        {row.smart_index_st ?? '-'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className="px-1.5 py-0.5 rounded bg-dark-600 text-dark-400 text-[10px]">{row.source}</span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {flipInfo && (
                          <div>
                            <span className={`px-2 py-0.5 rounded ${flipInfo.color} text-white text-[10px] font-bold`}>
                              {flipInfo.text}
                            </span>
                            <div className="text-[10px] text-dark-400 mt-0.5">
                              from SI={flipInfo.fromSI} @ {flipInfo.fromPrice != null ? flipInfo.fromPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                            </div>
                          </div>
                        )}
                        {row.is_flip && (
                          <button
                            onClick={() => handleFlipClick(row)}
                            className="px-2 py-0.5 rounded bg-blue-600 text-white text-[10px] font-bold hover:bg-blue-500 transition-colors"
                            title="Signal zone changed between pushes"
                          >
                            FLIP {isExpanded ? <ChevronUp className="inline w-3 h-3" /> : <ChevronDown className="inline w-3 h-3" />}
                          </button>
                        )}
                        {row.is_anomaly && (
                          <span
                            className="px-2 py-0.5 rounded bg-amber-600 text-white text-[10px] font-bold"
                            title="Signal may have changed for a wrong reason"
                          >
                            ANOMALY
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-center">
                      {row.short_signal === 'ENTRY' && <span className="px-2 py-0.5 rounded bg-orange-600 text-white text-[10px] font-bold">▼ ENTRY</span>}
                      {row.short_signal === 'ACTIVE' && <span className="px-2 py-0.5 rounded bg-orange-900/50 text-orange-400 text-[10px] font-bold">ACTIVE</span>}
                      {row.short_signal === 'EXIT' && <span className="px-2 py-0.5 rounded bg-yellow-600 text-white text-[10px] font-bold">✓ EXIT</span>}
                    </td>
                  </tr>
                  {isExpanded && flipDetail && (
                    <tr className="bg-dark-800/80">
                      <td colSpan={6} className="p-4">
                        <div className="text-xs text-dark-300 mb-2 font-semibold flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          All pushes for {row.symbol} on {formatDate(row.price_date)}
                        </div>
                        <div className="space-y-1">
                          {flipDetail.map((d, i) => {
                            const z = zoneColor(d.smart_index_st);
                            const isCurrent = d.id === row.id;
                            return (
                              <div
                                key={d.id}
                                className={`flex items-center gap-4 px-3 py-1.5 rounded ${
                                  isCurrent ? 'bg-red-900/20 border border-red-800/40' : 'bg-dark-700/50'
                                }`}
                              >
                                <span className="text-dark-500 w-16">{isCurrent ? 'CURRENT' : i === flipDetail.length - 1 ? 'FIRST' : `PUSH ${flipDetail.length - i}`}</span>
                                <span className={`px-2 py-0.5 rounded font-bold ${z.bg} ${z.text}`}>
                                  SI={d.smart_index_st ?? '-'} {z.label}
                                </span>
                                <span className="text-dark-400">Close={d.close != null ? d.close.toLocaleString() : '-'}</span>
                                <span className="text-dark-500 tabular-nums">@ {formatTimeOnly(d.computed_at)}</span>
                                {d.is_flip && <span className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold">FLIP</span>}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {data.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="py-12 text-center text-dark-500">
                  No signal history found. Adjust filters or wait for data pushes.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SignalHistoryPanel;
